import { useMemo } from "react";
import {
	Box,
	CircularProgress,
	Divider,
	Grid2,
	IconButton,
	MenuItem,
	Select,
	Tooltip,
	Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import type { ColDef, ValueFormatterParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import {
	useBarrelLayouts,
	useRollsetStatusOptions,
} from "../hooks/useRollshopOperations";
import type {
	BarrelLayout,
	RollsetListItem,
} from "../models/RollshopOperations";
import { computeAssignmentStatus } from "./RollsetsTable";
import type { RollPosition } from "./ChangeRollPositionModal";

const sectionHeaderSx = {
	color: "primary.main",
	fontWeight: 600,
	fontSize: "0.85rem",
	mt: 1.5,
	mb: 0.5,
};

const ASSIGNMENT_LABEL: Record<"ASSIGNED" | "PARTIAL" | "AVAILABLE", string> = {
	ASSIGNED: "Assigned",
	PARTIAL: "Partial",
	AVAILABLE: "Available",
};

interface Props {
	rollset: RollsetListItem | null;
	disabled?: boolean;
	disabledReason?: string;
	onChangeRollPosition: (
		position: RollPosition,
		currentRollId: number | null,
		currentRollName: string | null,
	) => void;
	onSetStatus: (statusCode: string) => void;
	isSettingStatus: boolean;
}

const fmt3 = (v: number | null | undefined) =>
	v == null ? "" : Number(v).toFixed(3);

// Inline `Label: value` row — paridad con el detail panel del legacy IHBox.
// Se usa una sola línea con label en gris + value bold a la derecha.
const InfoRow = ({
	label,
	value,
}: {
	label: string;
	value: string | number | null | undefined;
}) => (
	<Box sx={{ py: 0.25, lineHeight: 1.5 }}>
		<Typography
			component="span"
			variant="body2"
			color="text.secondary"
			sx={{ mr: 0.5 }}
		>
			{label}:
		</Typography>
		<Typography component="span" variant="body2" sx={{ fontWeight: 500 }}>
			{value == null ? "" : value}
		</Typography>
	</Box>
);

// Inline roll row with an Edit icon at the right that opens the
// Assign/Remove modal. Only renders when the position is active or has
// a roll (legacy doesn't show empty Roll 3/4/5 slots either).
interface RollInfoRowProps {
	label: string;
	rollName: string | null;
	rollId: number | null;
	disabled?: boolean;
	disabledReason?: string;
	onEdit: () => void;
}
const RollInfoRow = ({
	label,
	rollName,
	rollId,
	disabled = false,
	disabledReason,
	onEdit,
}: RollInfoRowProps) => {
	const hasRoll = !!rollName && rollId != null;
	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				py: 0.25,
				lineHeight: 1.5,
				gap: 0.5,
			}}
		>
			<Typography
				component="span"
				variant="body2"
				color="text.secondary"
				sx={{ mr: 0.5 }}
			>
				{label}:
			</Typography>
			<Typography
				component="span"
				variant="body2"
				sx={{ fontWeight: hasRoll ? 500 : 400, flex: 1 }}
			>
				{hasRoll ? rollName : ""}
			</Typography>
			<Tooltip
				title={
					disabled
						? disabledReason
						: hasRoll
							? "Change / remove roll"
							: "Assign roll"
				}
				placement="left"
			>
				<span>
					<IconButton
						size="small"
						onClick={onEdit}
						aria-label={`Change ${label}`}
						disabled={disabled}
						sx={{ p: 0.25 }}
					>
						<EditOutlinedIcon fontSize="small" />
					</IconButton>
				</span>
			</Tooltip>
		</Box>
	);
};

// Compact column set for the inline barrel-layout grid below. Mirrors what
// the legacy IHBox shows at the bottom of each rollset detail.
const BARREL_LAYOUT_COLS: ColDef<BarrelLayout>[] = [
	{ field: "Position", headerName: "#", width: 50, sort: "asc" },
	{
		field: "PassName",
		headerName: "Pass Id",
		flex: 1,
		minWidth: 130,
	},
	{ field: "PassTypeName", headerName: "Type", width: 90 },
	{ field: "PassShapeName", headerName: "Shape", width: 100 },
	{
		field: "PassScheduledTonnage",
		headerName: "Sched Tons",
		width: 110,
		cellStyle: { textAlign: "right" },
		valueFormatter: (p: ValueFormatterParams<BarrelLayout>) =>
			p.value != null
				? Number(p.value).toLocaleString("en-US", {
						minimumFractionDigits: 2,
						maximumFractionDigits: 2,
					})
				: "",
	},
];

export const RollsetDetailPanel = ({
	rollset,
	disabled = false,
	disabledReason,
	onChangeRollPosition,
	onSetStatus,
	isSettingStatus,
}: Props) => {
	const statusQuery = useRollsetStatusOptions();
	const barrelLayoutsQuery = useBarrelLayouts(rollset?.BarrelId ?? null);
	const barrelLayouts = useMemo(
		() => barrelLayoutsQuery.data ?? [],
		[barrelLayoutsQuery.data],
	);

	if (!rollset) {
		return (
			<Box
				sx={{
					height: "100%",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					p: 3,
					color: "text.secondary",
				}}
			>
				<Typography variant="body2">Select a rollset to see details</Typography>
			</Box>
		);
	}

	// Show Roll 3/4/5 only if assigned (paridad legacy: posiciones vacías
	// no se muestran). Top + Bottom siempre se muestran porque son las
	// posiciones primarias del rollset.
	const showRoll3 = rollset.Roll3Id != null || rollset.Roll3Name;
	const showRoll4 = rollset.Roll4Id != null || rollset.Roll4Name;
	const showRoll5 = rollset.Roll5Id != null || rollset.Roll5Name;

	return (
		<Box sx={{ p: 1.5, height: "100%", overflow: "auto" }}>
			{/* Header — solo el nombre, paridad legacy */}
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					mb: 1,
					gap: 1,
				}}
			>
				<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
					{rollset.Name}
				</Typography>
				{(isSettingStatus || barrelLayoutsQuery.isFetching) && (
					<CircularProgress size={16} />
				)}
			</Box>

			<Divider sx={{ mb: 1 }} />

			{/* When `disabled`, the edit IconButtons and the Op. Status select
			    surface the reason via their Tooltip. No banner needed. */}
			<Typography sx={{ ...sectionHeaderSx, mt: 0 }}>
				Basic Information
			</Typography>

			{/* Inline label:value pairs — replicates the legacy compact layout.
			    Order: Stand/Cartridge, Top/Bottom Roll (with edit), Top/Bottom
			    Diameter, Status/Op. Status. Roll 3/4/5 appear inline only
			    when assigned. */}
			<Grid2 container spacing={0} columnSpacing={2}>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<InfoRow label="Stand" value={rollset.StandName} />
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<InfoRow label="Cartridge" value={rollset.CartridgeName} />
				</Grid2>

				<Grid2 size={{ xs: 12, md: 6 }}>
					<RollInfoRow
						label="Top Roll"
						rollName={rollset.TopRollName}
						rollId={rollset.TopRollId}
						disabled={disabled}
						disabledReason={disabledReason}
						onEdit={() =>
							onChangeRollPosition(
								"TOP",
								rollset.TopRollId,
								rollset.TopRollName,
							)
						}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<RollInfoRow
						label="Bottom Roll"
						rollName={rollset.BottomRollName}
						rollId={rollset.BottomRollId}
						disabled={disabled}
						disabledReason={disabledReason}
						onEdit={() =>
							onChangeRollPosition(
								"BOTTOM",
								rollset.BottomRollId,
								rollset.BottomRollName,
							)
						}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12, md: 6 }}>
					<InfoRow label="Top Diameter" value={fmt3(rollset.TopRollDiameter)} />
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<InfoRow
						label="Bottom Diameter"
						value={fmt3(rollset.BottomRollDiameter)}
					/>
				</Grid2>

				<Grid2 size={{ xs: 12, md: 6 }}>
					<InfoRow
						label="Status"
						value={ASSIGNMENT_LABEL[computeAssignmentStatus(rollset)]}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					{/* Op. Status is inline-editable via a compact <Select>
					    (variant="standard" + disableUnderline keeps it
					    visually consistent with the surrounding label:value
					    rows while staying interactive). */}
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							py: 0.25,
							lineHeight: 1.5,
							gap: 0.5,
						}}
					>
						<Typography
							component="span"
							variant="body2"
							color="text.secondary"
							sx={{ mr: 0.5 }}
						>
							Op. Status:
						</Typography>
						<Tooltip
							title={disabled ? disabledReason : ""}
							disableHoverListener={!disabled}
							placement="left"
						>
							<Box component="span" sx={{ flex: 1, display: "flex" }}>
								<Select
									value={rollset.StatusCode ?? ""}
									onChange={(e) => onSetStatus(e.target.value)}
									size="small"
									variant="standard"
									disableUnderline
									disabled={disabled || isSettingStatus}
									sx={{
										flex: 1,
										fontSize: "0.875rem",
										fontWeight: 500,
										"& .MuiSelect-select": { py: 0 },
									}}
								>
									{(statusQuery.data ?? []).map((s) => (
										<MenuItem key={s.Code} value={s.Code}>
											{s.Name}
										</MenuItem>
									))}
								</Select>
							</Box>
						</Tooltip>
					</Box>
				</Grid2>

				{showRoll3 && (
					<Grid2 size={{ xs: 12, md: 6 }}>
						<RollInfoRow
							label="Roll 3"
							rollName={rollset.Roll3Name}
							rollId={rollset.Roll3Id}
							disabled={disabled}
							disabledReason={disabledReason}
							onEdit={() =>
								onChangeRollPosition("3", rollset.Roll3Id, rollset.Roll3Name)
							}
						/>
					</Grid2>
				)}
				{showRoll4 && (
					<Grid2 size={{ xs: 12, md: 6 }}>
						<RollInfoRow
							label="Roll 4"
							rollName={rollset.Roll4Name}
							rollId={rollset.Roll4Id}
							disabled={disabled}
							disabledReason={disabledReason}
							onEdit={() =>
								onChangeRollPosition("4", rollset.Roll4Id, rollset.Roll4Name)
							}
						/>
					</Grid2>
				)}
				{showRoll5 && (
					<Grid2 size={{ xs: 12, md: 6 }}>
						<RollInfoRow
							label="Roll 5"
							rollName={rollset.Roll5Name}
							rollId={rollset.Roll5Id}
							disabled={disabled}
							disabledReason={disabledReason}
							onEdit={() =>
								onChangeRollPosition("5", rollset.Roll5Id, rollset.Roll5Name)
							}
						/>
					</Grid2>
				)}
			</Grid2>

			{/* Barrel: <name> + inline read-only grid of the barrel's passes
			    (paridad legacy IHBox). Only when the rollset has a barrel. */}
			{rollset.BarrelId != null && (
				<>
					<Typography sx={sectionHeaderSx}>
						Barrel: {rollset.BarrelName ?? ""}
					</Typography>
					{barrelLayouts.length === 0 && !barrelLayoutsQuery.isFetching ? (
						<Box
							sx={{
								border: "1px solid #e0e0e0",
								borderRadius: 1,
								py: 2,
								color: "text.secondary",
								textAlign: "center",
							}}
						>
							<Typography variant="body2">No passes assigned</Typography>
						</Box>
					) : (
						<AgGridReact<BarrelLayout>
							rowData={barrelLayouts}
							columnDefs={BARREL_LAYOUT_COLS}
							defaultColDef={{
								resizable: true,
								sortable: false,
							}}
							rowHeight={28}
							headerHeight={28}
							domLayout="autoHeight"
							suppressContextMenu
							suppressCellFocus
						/>
					)}
				</>
			)}
		</Box>
	);
};
